import React from 'react';
import { palette } from '../theme';

interface SpinnerProps {
  size?: number;
  className?: string;
}

const KEYFRAME_ID = '__doer_spinner_keyframes';

const ensureKeyframes = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(KEYFRAME_ID)) return;
  const style = document.createElement('style');
  style.id = KEYFRAME_ID;
  style.textContent = '@keyframes doer-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
};

export const Spinner: React.FC<SpinnerProps> = ({ size = 40, className }) => {
  ensureKeyframes();

  const stroke = size * 0.2;
  const radius = (size - stroke) / 2;
  const center = size / 2;

  const arcStart = { x: center, y: center - radius };
  const arcEnd = { x: center + radius, y: center };

  const trackGradientId = React.useId();
  const arcGradientId = React.useId();

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      xmlns='http://www.w3.org/2000/svg'
      style={{ display: 'block', flexShrink: 0 }}
    >
      <defs>
        <linearGradient id={trackGradientId} x1='0' y1='0' x2='0' y2='1'>
          <stop offset='0%' stopColor={palette.primitives.purple.purple575} />
          <stop offset='100%' stopColor={palette.primitives.teal.teal500} />
        </linearGradient>
        <linearGradient id={arcGradientId} x1='0' y1='0' x2='0' y2='1'>
          <stop offset='0%' stopColor={palette.primitives.purple.purple550} />
          <stop offset='100%' stopColor={palette.primitives.blue.blue450} />
        </linearGradient>
      </defs>

      <circle
        cx={center}
        cy={center}
        r={radius}
        fill='none'
        stroke={`url(#${trackGradientId})`}
        strokeWidth={stroke}
        opacity={0.25}
      />

      <g style={{ transformOrigin: 'center', animation: 'doer-spin 0.9s linear infinite' }}>
        <path
          d={`M ${arcStart.x} ${arcStart.y} A ${radius} ${radius} 0 0 1 ${arcEnd.x} ${arcEnd.y}`}
          fill='none'
          stroke={`url(#${arcGradientId})`}
          strokeWidth={stroke}
          strokeLinecap='round'
          opacity={0.75}
        />
      </g>
    </svg>
  );
};

interface LoadingScreenProps {
  label?: string;
  overlay?: boolean;
}

export const LoadingScreen: React.FC<LoadingScreenProps> = ({ label, overlay }) => (
  <div
    style={{
      ...(overlay ? { position: 'absolute', inset: 0, zIndex: 5 } : { width: '100%', height: '100%' }),
      display: 'flex',
      flexDirection: 'column',
      gap: 14,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#F0F0F0'
    }}
  >
    <Spinner size={40} />
    {label && (
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: palette.primitives.slate.slate900
        }}
      >
        {label}
      </span>
    )}
  </div>
);
